import { useEffect, useState } from 'react'
import { useSetAtom } from 'jotai'
import { PartnerService, UserService } from '@duck/api-definitions'
import { invitationDataAtom, assignmentTokenAtom } from '@/store/userAtoms'
import { useAuth } from '@/hooks/useAuth'
import { useTar } from '@/modules'
import { SignupForm } from '@/components/SignupDialog/SignupForm'
import { GoogleSignInButton } from '@/components/LoginDialog/GoogleSignInButton'
import { LinkedInSignInButton } from '@/components/LoginDialog/LinkedInSignInButton'
import { InviteHeroCandidate } from './InviteHeroCandidate'

interface InviteGatePublicAssignmentChoiceProps {
  details: UserService.InvitationDetails
  token: string
}

export const InviteGatePublicAssignmentChoice = ({ details, token }: InviteGatePublicAssignmentChoiceProps) => {
  const { t } = useTar()
  const setInvitationData = useSetAtom(invitationDataAtom)
  const setAssignmentToken = useSetAtom(assignmentTokenAtom)
  const { openLoginDialog, signInWithGoogle, signInWithLinkedIn, error } = useAuth()
  const [showEmail, setShowEmail] = useState(false)

  useEffect(() => {
    setInvitationData(details)
    setAssignmentToken(token)
    return () => {
      setInvitationData(null)
      setAssignmentToken(null)
    }
  }, [details, token, setInvitationData, setAssignmentToken])

  const orgName = details.organizationName ?? ''

  return (
    <div className="w-full max-w-md space-y-6">
      <InviteHeroCandidate
        organizationName={orgName}
        logoURL={details.organizationLogoURL ?? undefined}
        logoCanvas={details.organizationLogoCanvas as PartnerService.OrganizationLogoCanvas | undefined}
        headline={t('invite.publicAssignment.title', { organizationName: orgName })}
      />

      {!showEmail && (
        <div className="space-y-3">
          <LinkedInSignInButton onClick={() => signInWithLinkedIn?.()} isDisabled={false} />
          <GoogleSignInButton onClick={() => signInWithGoogle?.()} isDisabled={false} />
        </div>
      )}

      {error && (
        <div className="bg-error/10 border border-error/20 rounded-lg p-3 text-sm text-error">
          {error}
        </div>
      )}

      {!showEmail && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={() => setShowEmail(true)}
            className="text-sm text-accent hover:text-foreground"
          >
            {t('auth.continueWithEmail')}
          </button>
        </div>
      )}

      {showEmail && <SignupForm />}

      <div className="pt-4 border-t border-border/40 text-center">
        <p className="text-sm text-muted-foreground">
          {t('invite.publicAssignment.alreadyHaveAccount')}{' '}
          <button
            type="button"
            onClick={openLoginDialog}
            className="text-accent hover:text-foreground font-medium"
          >
            {t('invite.publicAssignment.loginLink')}
          </button>
        </p>
      </div>
    </div>
  )
}
